/**
 * Form Tracking Utilities
 * Pushes field changes, button clicks, step views, and form completion to the dataLayer.
 * Used by the ApplicationWizard component.
 */

const STEP_NAMES = {
  1: 'Personal Information',
  2: 'Educational Information',
  3: 'Thank You'
};

const getLanguage = () => {
  if (typeof document !== 'undefined') {
    return document.documentElement.lang || document.querySelector('html')?.getAttribute('lang') || 'en';
  }
  return 'en';
};

const pushToDataLayer = (data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...data, page_language: getLanguage() });
};

/**
 * Track a field value change
 * @param {string} fieldName - Human-readable field name
 * @param {string} fieldValue - The selected/entered value
 * @param {number} formStep - Current step number
 */
export const trackFieldChange = (fieldName, fieldValue, formStep) => {
  pushToDataLayer({
    event: 'field_change',
    field_name: fieldName,
    field_value: fieldValue || '',
    form_step: formStep,
    step_name: STEP_NAMES[formStep] || ''
  });
};

/**
 * Track step view when a step loads
 * @param {number} formStep - Current step number
 */
export const trackStepView = (formStep) => {
  pushToDataLayer({
    event: 'step_view',
    form_step: formStep,
    step_name: STEP_NAMES[formStep] || ''
  });
};

/**
 * Track Next button click
 * @param {number} formStep - Current step number
 * @param {Object} fieldValues - Current field values
 */
export const trackNextClick = (formStep, fieldValues = {}) => {
  pushToDataLayer({
    event: 'next_button_click',
    form_step: formStep,
    step_name: STEP_NAMES[formStep] || '',
    fields: fieldValues
  });
};

/**
 * Track Submit button click
 * @param {number} formStep - Current step number
 * @param {Object} fieldValues - Current field values
 */
export const trackSubmitClick = (formStep, fieldValues = {}) => {
  pushToDataLayer({
    event: 'submit_button_click',
    form_step: formStep,
    step_name: STEP_NAMES[formStep] || '',
    fields: fieldValues
  });
};

/**
 * Track form completion (Thank You page)
 */
export const trackFormComplete = () => {
  pushToDataLayer({
    event: 'form_complete',
    form_step: 3,
    step_name: STEP_NAMES[3]
  });
};

export default {
  trackFieldChange,
  trackStepView,
  trackNextClick,
  trackSubmitClick,
  trackFormComplete
};
